import { useEffect, useRef, useState } from "react";
import {
  generateCompositeImage,
  LABEL_FONT_FAMILIES,
  LABEL_FONT_WEIGHTS,
} from "../../utils/compositeImage";
import {
  DraggableLabel,
  DraggableOverlay,
  LabelTextInput,
  ColorModeToggle,
  ColorPicker,
  DEFAULT_LABEL_VALUES,
} from "../MovieRowForm/HighlightRowBuilder";

const DEFAULT_FIELDS = {
  overlayXPct: 0.35,
  overlayYPct: 0.35,
  overlayWidthPct: 0.3,
  overlayHeightPct: 0.3,
  alignment: "center",
  ...DEFAULT_LABEL_VALUES,
};

// A popup that lets the admin overlay a plain image file (jpg/png/webp — not
// another tile) and/or a text badge onto one already-picked image, reusing
// the exact drag/alignment/label-styling pieces from the Highlight row
// builder. Saving hands the composited Blob back up as a File; the caller is
// responsible for adding it into the field's array like any other upload.
const ImageOverlayModal = ({ baseImageUrl, targetDims, onClose, onSave }) => {
  const previewRef = useRef(null);
  const requestIdRef = useRef(0);
  const previewUrlRef = useRef(null);

  const [overlayFile, setOverlayFile] = useState(null);
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dims, setDims] = useState({ width: null, height: null });
  const [generating, setGenerating] = useState(false);
  const [resultBlob, setResultBlob] = useState(null);

  const patchFields = (patch) => setFields((f) => ({ ...f, ...patch }));

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (!baseImageUrl) return;
    const myRequestId = ++requestIdRef.current;
    setGenerating(true);
    const timer = setTimeout(async () => {
      const overlayImgUrl = overlayFile ? URL.createObjectURL(overlayFile) : null;
      try {
        const hasLabel = Boolean(fields.labelText && fields.labelText.trim());
        const result = await generateCompositeImage({
          backgroundUrl: baseImageUrl,
          overlayUrl: overlayImgUrl,
          alignment: fields.alignment,
          overlayXPct: fields.overlayXPct,
          overlayYPct: fields.overlayYPct,
          overlayWidthPct: fields.overlayWidthPct,
          overlayHeightPct: fields.overlayHeightPct,
          targetWidth: targetDims?.width,
          targetHeight: targetDims?.height,
          // This modal overlays onto real tile photos, never purpose-built
          // templates with an actual designed cutout — always free
          // drag/resize placement, never auto-fill-a-detected-region mode.
          disableCutoutDetection: true,
          label: hasLabel
            ? {
                text: fields.labelText,
                xPct: fields.labelXPct,
                yPct: fields.labelYPct,
                fontFamily: fields.labelFontFamily,
                fontSize: fields.labelFontSize,
                fontWeight: fields.labelFontWeight,
                fontStyle: fields.labelFontStyle,
                showBackground: fields.labelShowBackground,
                textColorType: fields.labelTextColorType,
                textColor: fields.labelTextColor,
                textGradientFrom: fields.labelTextGradientFrom,
                textGradientTo: fields.labelTextGradientTo,
                bgColorType: fields.labelBgColorType,
                bgColor: fields.labelBgColor,
                bgGradientFrom: fields.labelBgGradientFrom,
                bgGradientTo: fields.labelBgGradientTo,
              }
            : null,
        });
        if (requestIdRef.current !== myRequestId) return; // a newer edit already superseded this one
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const nextPreviewUrl = URL.createObjectURL(result.blob);
        previewUrlRef.current = nextPreviewUrl;
        setPreviewUrl(nextPreviewUrl);
        setDims({ width: result.width, height: result.height });
        setResultBlob(result.blob);
      } catch (err) {
        console.error("Failed to generate overlay preview:", err);
      } finally {
        if (overlayImgUrl) URL.revokeObjectURL(overlayImgUrl);
        if (requestIdRef.current === myRequestId) setGenerating(false);
      }
    }, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseImageUrl, overlayFile, fields, targetDims]);

  const hasLabel = Boolean(fields.labelText && fields.labelText.trim());
  const hasChange = Boolean(overlayFile) || hasLabel;

  // DraggableOverlay/DraggableLabel only ever check `slot.overlayTid` for
  // truthiness (whether an overlay has been picked at all) — a real tile id
  // isn't required, so a plain boolean sentinel is enough here.
  const slotForDrag = {
    overlayTid: Boolean(overlayFile),
    overlayXPct: fields.overlayXPct,
    overlayYPct: fields.overlayYPct,
    overlayWidthPct: fields.overlayWidthPct,
    overlayHeightPct: fields.overlayHeightPct,
    labelText: fields.labelText,
    labelXPct: fields.labelXPct,
    labelYPct: fields.labelYPct,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Overlay Image / Text</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        <div
          ref={previewRef}
          className="relative rounded overflow-hidden bg-gray-100 mb-3"
          style={{
            aspectRatio:
              dims.width && dims.height
                ? `${dims.width} / ${dims.height}`
                : targetDims
                ? `${targetDims.width} / ${targetDims.height}`
                : "16 / 9",
          }}
        >
          <img
            src={previewUrl || baseImageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          {generating && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-xs">
              Rendering…
            </div>
          )}
          {overlayFile && (
            <DraggableOverlay
              slot={slotForDrag}
              containerRef={previewRef}
              onCommit={(box) =>
                patchFields({
                  overlayXPct: box.xPct,
                  overlayYPct: box.yPct,
                  overlayWidthPct: box.widthPct,
                  overlayHeightPct: box.heightPct,
                })
              }
            />
          )}
          <DraggableLabel
            slot={slotForDrag}
            containerRef={previewRef}
            onCommit={(xPct, yPct) => patchFields({ labelXPct: xPct, labelYPct: yPct })}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-600">Overlay Image (JPG / PNG / WebP)</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setOverlayFile(file);
              patchFields({ overlayXPct: 0.35, overlayYPct: 0.35, overlayWidthPct: 0.3, overlayHeightPct: 0.3 });
            }}
            className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-indigo-100 file:text-indigo-700"
          />
          {overlayFile && (
            <button
              type="button"
              onClick={() => setOverlayFile(null)}
              className="text-xs text-red-500 hover:text-red-700"
            >
              Remove overlay image
            </button>
          )}

          {overlayFile && (
            <p className="text-[10px] text-gray-400">
              Drag the overlay on the preview above to position and resize it.
            </p>
          )}

          <div className="pt-2 border-t border-gray-100 space-y-1">
            <label className="block text-[10px] font-medium text-gray-500">Badge Text</label>
            <LabelTextInput value={fields.labelText} onCommit={(text) => patchFields({ labelText: text })} />

            {hasLabel && (
              <div className="space-y-2 rounded border border-gray-100 bg-gray-50 p-2 mt-1">
                <p className="text-[10px] text-gray-400 -mt-0.5">
                  Drag the badge on the preview above to position it, or set exactly:
                </p>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-[10px] text-gray-500">
                    X%
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round((fields.labelXPct ?? 0.05) * 100)}
                      onChange={(e) =>
                        patchFields({
                          labelXPct: Math.min(Math.max(Number(e.target.value) || 0, 0), 100) / 100,
                        })
                      }
                      className="w-14 text-xs py-0.5 px-1 border rounded border-gray-300"
                    />
                  </label>
                  <label className="flex items-center gap-1 text-[10px] text-gray-500">
                    Y%
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round((fields.labelYPct ?? 0.05) * 100)}
                      onChange={(e) =>
                        patchFields({
                          labelYPct: Math.min(Math.max(Number(e.target.value) || 0, 0), 100) / 100,
                        })
                      }
                      className="w-14 text-xs py-0.5 px-1 border rounded border-gray-300"
                    />
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-[10px] text-gray-500">
                    Font
                    <select
                      value={fields.labelFontFamily}
                      onChange={(e) => patchFields({ labelFontFamily: e.target.value })}
                      className="w-full text-xs py-1 px-1 border rounded border-gray-300 mt-0.5"
                    >
                      {LABEL_FONT_FAMILIES.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-[10px] text-gray-500">
                    Weight
                    <select
                      value={fields.labelFontWeight}
                      onChange={(e) => patchFields({ labelFontWeight: e.target.value })}
                      className="w-full text-xs py-1 px-1 border rounded border-gray-300 mt-0.5"
                    >
                      {LABEL_FONT_WEIGHTS.map((w) => (
                        <option key={w.value} value={w.value}>
                          {w.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1 text-[10px] text-gray-500">
                    Size (px)
                    <input
                      type="number"
                      min={8}
                      placeholder="Auto"
                      value={fields.labelFontSize || ""}
                      onChange={(e) =>
                        patchFields({ labelFontSize: e.target.value ? Number(e.target.value) : null })
                      }
                      className="w-16 text-xs py-0.5 px-1 border rounded border-gray-300"
                    />
                  </label>
                  <label className="flex items-center gap-1 text-[10px] text-gray-500">
                    <input
                      type="checkbox"
                      checked={fields.labelFontStyle === "italic"}
                      onChange={(e) => patchFields({ labelFontStyle: e.target.checked ? "italic" : "normal" })}
                    />
                    Italic
                  </label>
                </div>

                <label className="flex items-center gap-1 text-[10px] text-gray-500">
                  <input
                    type="checkbox"
                    checked={fields.labelShowBackground}
                    onChange={(e) => patchFields({ labelShowBackground: e.target.checked })}
                  />
                  Show background pill
                </label>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-medium text-gray-500">Text Color</span>
                    <ColorModeToggle
                      mode={fields.labelTextColorType}
                      onChange={(m) => patchFields({ labelTextColorType: m })}
                    />
                  </div>
                  <ColorPicker
                    mode={fields.labelTextColorType}
                    solid={fields.labelTextColor}
                    from={fields.labelTextGradientFrom}
                    to={fields.labelTextGradientTo}
                    onSolidChange={(v) => patchFields({ labelTextColor: v })}
                    onFromChange={(v) => patchFields({ labelTextGradientFrom: v })}
                    onToChange={(v) => patchFields({ labelTextGradientTo: v })}
                  />
                </div>

                {fields.labelShowBackground && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-medium text-gray-500">Background Color</span>
                      <ColorModeToggle
                        mode={fields.labelBgColorType}
                        onChange={(m) => patchFields({ labelBgColorType: m })}
                      />
                    </div>
                    <ColorPicker
                      mode={fields.labelBgColorType}
                      solid={fields.labelBgColor}
                      from={fields.labelBgGradientFrom}
                      to={fields.labelBgGradientTo}
                      onSolidChange={(v) => patchFields({ labelBgColor: v })}
                      onFromChange={(v) => patchFields({ labelBgGradientFrom: v })}
                      onToChange={(v) => patchFields({ labelBgGradientTo: v })}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!resultBlob || generating || !hasChange}
            onClick={() => {
              const file = new File([resultBlob], `overlay_${Date.now()}.webp`, { type: "image/webp" });
              onSave(file);
            }}
            className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Add to Images
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageOverlayModal;
